import React from 'react';
import { Card, Row, Col } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// OTR Performance threshold values - ensure these match OTRDashboard
const OTR_THRESHOLDS = {
  EARLY_MINUTES: -2,   // Early threshold in minutes (negative value)
  LATE_MINUTES: 5      // Late threshold in minutes (positive value)
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const OTRCharts = ({ data }) => {
  // Prepare data for stacked bar chart (On-time / Early / Late)
  const prepareStackedBarData = () => {
    if (!data) return [];
    
    // Group data by time periods
    const groupedByPeriod = data.reduce((acc, item) => {
      const period = getTimePeriod(item.date);
      if (!acc[period]) {
        acc[period] = [];
      }
      acc[period].push(item);
      return acc;
    }, {});
    
    // Calculate on-time percentages for each period
    return Object.keys(TIME_PERIODS).map(period => {
      const periodData = groupedByPeriod[period] || [];
      const total = periodData.length;
      
      if (total === 0) {
        return {
          period: TIME_PERIODS[period].label,
          onTime: 0,
          early: 0,
          late: 0
        };
      }
      
      const onTime = periodData.filter(item => 
        item.ARRIVAL_TIME_VARIANCE >= OTR_THRESHOLDS.EARLY_MINUTES && item.ARRIVAL_TIME_VARIANCE <= OTR_THRESHOLDS.LATE_MINUTES
      ).length;
      
      const early = periodData.filter(item => 
        item.ARRIVAL_TIME_VARIANCE < OTR_THRESHOLDS.EARLY_MINUTES
      ).length;
      
      const late = periodData.filter(item => 
        item.ARRIVAL_TIME_VARIANCE > OTR_THRESHOLDS.LATE_MINUTES
      ).length;
      
      return {
        period: TIME_PERIODS[period].label,
        onTime: (onTime / total) * 100,
        early: (early / total) * 100,
        late: (late / total) * 100
      };
    });
  };

  // Prepare data for line chart (trend over time)
  const prepareTrendData = () => {
    if (!data) return [];
    
    // Group data by date
    const groupedByDate = data.reduce((acc, item) => {
      const date = item.DATE_ID;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(item);
      return acc;
    }, {});
    
    // Calculate on-time percentage for each date
    return Object.entries(groupedByDate).map(([date, items]) => ({
      date,
      onTimePercentage: calculateOnTimePercentage(items)
    })).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Prepare data for pie chart (stop type distribution)
  const preparePieData = () => {
    if (!data) return [];
    
    const stopTypes = {
      first: data.filter(item => item.TSN_FIRST_LAST_IND === 'F'),
      mid: data.filter(item => item.TSN_FIRST_LAST_IND === 'M'),
      last: data.filter(item => item.TSN_FIRST_LAST_IND === 'L')
    };
    
    return [
      {
        name: 'First Stop',
        value: calculateOnTimePercentage(stopTypes.first)
      },
      {
        name: 'Mid Stop',
        value: calculateOnTimePercentage(stopTypes.mid)
      },
      {
        name: 'Last Stop',
        value: calculateOnTimePercentage(stopTypes.last)
      }
    ];
  };

  const stackedBarData = prepareStackedBarData();
  const trendData = prepareTrendData();
  const pieData = preparePieData();

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card title="On-Time Performance by Time Period">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={stackedBarData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="onTime" name="On Time" stackId="a" fill="#00C49F" />
              <Bar dataKey="early" name="Early" stackId="a" fill="#FFBB28" />
              <Bar dataKey="late" name="Late" stackId="a" fill="#FF8042" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      <Col span={24}>
        <Card title="On-Time Performance Trend">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={trendData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="onTimePercentage"
                name="On-Time %"
                stroke="#0088FE"
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      <Col span={24}>
        <Card title="On-Time Performance by Stop Type">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </Col>
    </Row>
  );
};

// Time periods classification for analytics
const TIME_PERIODS = {
  MORNING: { start: '06:00', end: '10:00', label: 'Morning Peak' },
  MIDDAY: { start: '10:01', end: '15:00', label: 'Midday' },
  AFTERNOON: { start: '15:01', end: '19:00', label: 'Afternoon Peak' },
  EVENING: { start: '19:01', end: '23:59', label: 'Evening' },
  NIGHT: { start: '00:00', end: '05:59', label: 'Night' }
};

// Helper function to determine time period from a time string
const getTimePeriod = (timeString) => {
  if (!timeString) return 'unknown';
  
  try {
    // Extract time part from datetime string
    let timePart = timeString;
    if (timeString.includes(' ')) {
      timePart = timeString.split(' ')[1];
    }
    
    // Default to empty string if splitting fails
    if (!timePart) return 'unknown';
    
    // Extract hours:minutes
    const timeForComparison = timePart.substring(0, 5);
    
    for (const [period, { start, end }] of Object.entries(TIME_PERIODS)) {
      if (timeForComparison >= start && timeForComparison <= end) {
        return period;
      }
    }
    
    return 'unknown';
  } catch (error) {
    console.warn('Error determining time period:', error);
    return 'unknown';
  }
};

// Calculate on-time percentage for a given set of stops
const calculateOnTimePercentage = (stops) => {
  if (!stops || stops.length === 0) return 0;
  
  const onTimeStops = stops.filter(stop => {
    const variance = stop.ARRIVAL_TIME_VARIANCE;
    // Use OTR_THRESHOLDS constants
    return variance >= OTR_THRESHOLDS.EARLY_MINUTES && variance <= OTR_THRESHOLDS.LATE_MINUTES;
  });
  
  return (onTimeStops.length / stops.length) * 100;
};

export default OTRCharts; 